<template>
  <v-app>
    <v-row class="px-4">
      <LoadingDialog v-if="loading" :is_loading="loading"></LoadingDialog>

      <!-- Graphs -->
      <v-col cols="12">
        <v-card v-if="!loading" elevation="0" style="margin-top: 15px">
          <v-card-title>
            <v-row class="d-flex flex-wrap">
              <!-- Expert Name Column -->
              <v-col cols="12" sm="6" md="8">
                {{ $t("_module_expert_") }} :
                {{ $t(this.currentExpert) }}
              </v-col>

              <!-- Date Selector Column -->
              <v-col cols="12" sm="6" md="2">
                <dates-selector-expert
                  :store_date="$store.getters.dates"
                  :min_date="auditDates.start"
                  :max_date="auditDates.end"
                  :show_label="false"
                  :default_last_month="false"
                  store_cal_event="SET_EXPERT_CALENDAR_REFRESH"
                  store_date_commit="SET_EXPERT_DATES"
                />
              </v-col>

              <!-- Tree Select Column (first condition) -->
              <v-col cols="12" sm="6" md="2" v-if="has_pdm && ac == false">
                <tree-select
                  v-if="has_pdm"
                  v-model="ts_values"
                  :auto-load-root-options="false"
                  :defaultExpandLevel="expand_level"
                  :disable-branch-nodes="true"
                  :load-options="getAllPdmTree"
                  :multiple="false"
                  :options="ts_options"
                  :showCount="true"
                  :placeholder="$t('_selectionner_un_point_de_mesure_')"
                  v-on:input="changePdm(ts_values)"
                >
                  <div slot="value-label" slot-scope="{ node }">
                    {{ node.raw.customLabel }}
                  </div>
                </tree-select>
              </v-col>

              <!-- Tree Select Column (second condition) -->
              <v-col cols="12" sm="6" md="2" v-if="has_pdm && ac == true">
                <tree-select
                  v-if="has_pdm"
                  v-model="ts_values"
                  :auto-load-root-options="false"
                  :defaultExpandLevel="expand_level"
                  :disable-branch-nodes="false"
                  :load-options="getAllPdmTree"
                  :multiple="false"
                  :options="filterTree(ts_options)"
                  :showCount="true"
                  :placeholder="$t('_selectionner_un_point_de_mesure_')"
                  v-on:input="changePdm(ts_values)"
                >
                  <div slot="value-label" slot-scope="{ node }">
                    {{ node.raw.customLabel }}
                  </div>
                </tree-select>
              </v-col>
            </v-row>
          </v-card-title>
        </v-card>

        <!-- Additional Content -->
        <v-card v-if="!loading" elevation="0">
          <v-card-text>
            <plotly-graph
              :dates="expertDates"
              :graph_data="graph_data_1"
              style="padding: 20px"
            />
            <v-btn v-if="has_pdm" @click="downloadData()">{{
              $t("_telecharger_le_tableau_comparatif_")
            }}</v-btn>
            <plotly-graph
              v-if="showGraph2"
              :dates="expertDates"
              :graph_data="graph_data_2"
              style="padding: 20px"
            />
            <v-btn v-if="showGraph2" @click="downloadDataPdm()">{{
              $t("_telecharger_le_tableau_comparatif_")
            }}</v-btn>
            <plotly-graph
              v-if="showGraph3"
              :dates="expertDates"
              :graph_data="graph_data_3"
              style="padding: 20px"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-app>
</template>

<script>
import api from "@/api/api";
import cd from "@/utils/compute_data.js";
import LoadingDialog from "@/components/Utils/LoadingDialog";
import PlotlyGraph from "../../components/Plotly/PlotlyGraph";
import TreeSelect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import DatesSelectorExpert from "@/components/Utils/DatesSelector";
import { mapGetters } from "vuex";

export default {
  components: {
    LoadingDialog,
    DatesSelectorExpert,
    PlotlyGraph,
    TreeSelect,
  },
  props: {
    expertName: {
      type: String,
      required: null,
    },
    installationId: {
      type: [Number, String],
      required: null,
    },
  },

  data() {
    return {
      loading: false,
      ac: false,

      // Params
      installation_id: null,
      expand_level: 0,

      // Graphics
      current_pdm: this.$store.getters.expert_pdm,
      has_pdm: false,

      graph_data_1: {},
      graph_data_2: {},
      graph_data_3: {},

      hasOneGraph: false,
      showGraph2: false,
      showGraph3: false,

      startMonth: null,

      ts_values: this.$store.getters.expert_pdm,
      ts_options: [],
    };
  },

  mounted() {
    this.installation_id = this.currentInstallationId;

    this.searchacsite();

    this.$store.dispatch("setInstallationExpertDate", {
      start_date: this.$store.state.parametersStore.dates.start,
      end_date: this.$store.state.parametersStore.dates.end,
    });

    this.test_production();
  },

  computed: {
    ...mapGetters(["role", "installations", "cur_install_id", "constants"]),
    currentExpert() {
      return this.expertName || this.$route.params.expert;
    },

    currentInstallationId() {
      return Number(this.installationId || this.$route.params.id);
    },

    installation() {
      return this.$store.getters.installations.find(
        (x) => x.id == this.currentInstallationId
      );
    },

    expertDates() {
      return this.$store.state.parametersStore.dates;
    },

    refreshCalendar() {
      return this.$store.state.parametersStore.expert_calendar_refresh;
    },

    auditDates() {
      return this.$store.state.parametersStore.audit_dates;
    },
  },

  watch: {
    $route: {
      async handler() {
        // Set Expert date for routing
        if (this.expertDates.start === null || this.expertDates.end === null) {
          this.$store.dispatch("setInstallationExpertDate", {
            start_date: this.installation.audit_start_date,
            end_date: this.installation.audit_end_date,
          });
        }
        await this.hasPdm();
        await this.getAllPdmTree();
        await this.renderGraphModel();
        await this.refreshPdm();
      },
      immediate: true,
    },

    "$store.getters.expertCalendarRefresh": {
      async handler() {
        if (this.$store.getters.expertCalendarRefresh) {
          this.$store.commit("SET_EXPERT_CALENDAR_REFRESH", false);

          this.$store.dispatch("setInstallationAuditDate", {
            start_date: this.$store.state.parametersStore.expert_dates.start,
            end_date: this.$store.state.parametersStore.expert_dates.end,
          });
          await this.renderGraphModel();
          await this.refreshPdm();
        }
      },
      immediate: true,
    },

    "$root.$i18n.locale": {
      async handler() {
        await this.getAllPdmTree();
        await this.renderGraphModel();
        await this.refreshPdm();
      },
    },
  },

  methods: {
    test_production() {
      const diffTime = Math.abs(
        new Date(this.$store.state.parametersStore.dates.start) -
          new Date(this.$store.state.parametersStore.dates.end)
      );
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const diffMonths = (diffDays / 30.44).toFixed(2);
      if (this.currentExpert == "_production_") {
        if (diffMonths > 3) {
          const newStart = new Date(
            this.$store.state.parametersStore.dates.end
          );
          newStart.setMonth(newStart.getMonth() - 3);
          const formattedStart = newStart.toISOString().slice(0, 10);
          console.log(
            formattedStart,
            this.$store.state.parametersStore.dates.end
          );
          this.$store.dispatch("setInstallationAuditDate", {
            start_date: formattedStart,
            end_date: this.$store.state.parametersStore.dates.end,
          });
          this.$store.dispatch("setInstallationExpertDate", {
            start_date: formattedStart,
            end_date: this.$store.state.parametersStore.dates.end,
          });
        }
      }
    },

    searchacsite() {
      this.installations.forEach((element) => {
        if (
          element.installation_id == this.cur_install_id &&
          element.dc_analysis == false
        ) {
          this.ac = true;
        }
      });
    },

    takeauditdate() {
      let res = {
        start: "",
        end: "",
      };

      this.installations.forEach((elm) => {
        if (elm.installation_id == this.cur_install_id) {
          res.start = elm.audit_start_date;
          res.end = elm.audit_end_date;
        }
      });
      return res;
    },

    filterTree(tree) {
      // console.log("Arborescence avant filtrage et modification d'ID:", JSON.stringify(tree, null, 2));
      let counter = 1;

      return tree
        .map((node) => {
          const newNode = { ...node };
          if (newNode.label && newNode.label.includes("Onduleur")) {
            newNode.id = `p-1|inv-${
              newNode.id.split("|")[1].split("-")[1]
            }|mpp-${counter}-1`;
            counter++;
          }
          if (newNode.label && newNode.label.includes("PdM")) {
            return null;
          }
          if (newNode.children) {
            newNode.children = this.filterTree(newNode.children);
            if (newNode.children.length === 0) {
              delete newNode.children;
            }
          }
          return newNode;
        })
        .filter(Boolean);
    },

    downloadData() {
      let e = 0;

      if ("data_render" in this.graph_data_1) {
        for (e in this.graph_data_1["data_render"]) {
          api
            .convertJsonExpert(
              { data: this.graph_data_1["data_render"][e] },
              "xlsx",
              "plotly_matrix",
              this.currentExpert,
              this.$root.$i18n.locale
            )
            .then((res) => {
              let name =
                this.$t(this.currentExpert) +
                "_" +
                this.currentInstallationId +
                "_" +
                this.$store.getters.expertDates.start +
                "__" +
                this.$store.getters.expertDates.end +
                ".xlsx";
              let a = document.createElement("a");
              a.href = URL.createObjectURL(res.data);
              a.download = name;
              a.click();
            });
        }
      }
    },

    downloadDataPdm() {
      if ("data_render" in this.graph_data_2) {
        for (let e in this.graph_data_2["data_render"]) {
          api
            .convertJsonExpert(
              { data: this.graph_data_2["data_render"][e] },
              "xlsx",
              "plotly_vector",
              this.currentExpert,
              this.$root.$i18n.locale
            )
            .then((res) => {
              let name = "file.xlsx";
              if (this.current_pdm != null) {
                name =
                  this.$t(this.currentExpert) +
                  "_" +
                  this.current_pdm +
                  "_" +
                  this.currentInstallationId +
                  "_" +
                  this.$store.getters.expertDates.start +
                  "__" +
                  this.$store.getters.expertDates.end +
                  ".xlsx";
                let a = document.createElement("a");
                a.href = URL.createObjectURL(res.data);
                a.download = name;
                a.click();
              }
            });
        }
      }
    },

    async changePdm(pdm) {
      if (pdm !== undefined && pdm !== null) {
        this.current_pdm = pdm;
      } else {
        this.current_pdm = null;
      }

      this.$store.commit("SET_EXPERT_PDM", this.current_pdm);
      await this.refreshPdm();
    },

    getMenu() {
      return this.$store.getters.installations.find(
        (x) => x.id == this.currentInstallationId
      )?.target_menu;
    },

    hasPdm() {
      this.has_pdm = false;

      api.hasPdmReport(this.currentExpert).then((res_mod) => {
        this.has_pdm = res_mod.data;
      });
    },

    async getAllPdmTree() {
      const langcode = this.$root.$i18n.locale;

      await api.getPdmTree(this.installation_id, langcode).then((res) => {
        this.ts_options = [];
        res.data["tree"].map((elm) => this.ts_options.push(elm));
        this.expand_level = res.data["expand_level"];
      });

      const pacgrid = this.findPacGrid(this.ts_options);

      if (pacgrid) {
        this.ts_values = pacgrid.id;
        await this.changePdm(this.ts_values);
      }
    },
    findPacGrid(nodes) {
      for (const node of nodes) {
        // Search by label
        if (
          typeof node.label === "string" &&
          node.label.trim().toLowerCase() === "pac grid"
        ) {
          return node;
        }

        // Search inside children
        if (node.children && node.children.length) {
          const found = this.findPacGrid(node.children);

          if (found) {
            return found;
          }
        }
      }

      return null;
    },

    async renderGraphModel() {
      this.loading = true;

      let graphs = null;
      this.showGraph2 = false;
      this.showGraph3 = false;

      await api
        .getReportFullName(this.currentExpert, this.getMenu())
        .then((res_mod) => {
          this.hasOneGraph = res_mod.data.length === 1;
          graphs = res_mod.data;
        });

      this.query_param = {};
      this.query_param["installation_id"] = this.installation.id;
      this.query_param["start_date"] = this.$store.getters.expertDates.start;
      this.query_param["end_date"] = this.$store.getters.expertDates.end;
      this.query_param["date_interval"] = null;
      this.query_param["export_type"] = null;
      this.query_param["pacgrid"] =
        this.installation.elec_var.includes("Pac@grid");
      if (graphs !== undefined) {
        // Graph 1
        this.query_param["sel_pdm"] = null;
        await api
          .getTemplateRenderByName(
            graphs[0][1],
            this.$root.$i18n.locale,
            this.query_param
          )
          .then((res) => {
            this.graph_data_1 = {
              data_render: res.data.data,
              layout_render: res.data.layout,
            };
          });
      }

      this.loading = false;
    },

    async refreshPdm() {
      let graphs = null;
      this.showGraph2 = false;
      this.showGraph3 = false;

      if (this.current_pdm == null) return;

      this.loading = true;

      await api
        .getReportFullName(this.currentExpert, this.getMenu())
        .then((res_mod) => {
          graphs = res_mod.data;
        });

      this.query_param = {};
      this.query_param["installation_id"] = this.installation.id;
      this.query_param["start_date"] = this.$store.getters.expertDates.start;
      this.query_param["end_date"] = this.$store.getters.expertDates.end;
      this.query_param["date_interval"] = null;
      this.query_param["export_type"] = null;
      this.query_param["pacgrid"] =
        this.installation.elec_var.includes("Pac@grid");
      if (graphs !== undefined) {
        // Graph 2
        if (this.current_pdm !== null && graphs.length >= 2) {
          this.query_param["sel_pdm"] = cd.parsePdmId(this.current_pdm);
          await api
            .getTemplateRenderByName(
              graphs[1][2],
              this.$root.$i18n.locale,
              this.query_param
            )
            .then((res) => {
              this.graph_data_2 = {
                data_render: res.data.data,
                layout_render: res.data.layout,
              };
              this.showGraph2 = true;
            });
        }

        // Graph 3
        if (this.current_pdm !== null && graphs.length >= 3) {
          this.query_param["sel_pdm"] = cd.parsePdmId(this.current_pdm);
          await api
            .getTemplateRenderByName(
              graphs[2][3],
              this.$root.$i18n.locale,
              this.query_param
            )
            .then((res) => {
              this.graph_data_3 = {
                data_render: res.data.data,
                layout_render: res.data.layout,
              };
              this.showGraph3 = true;
            });
        }
      }

      this.loading = false;
    },
  },
};
</script>

<style scoped>
.v-select-like {
  border: 1px solid black !important;
  width: 100%;
  display: block;
  border-radius: 4px;
  outline: none;
  color: black;
}

.vue-treeselect {
  font-size: 0.9rem;
  font-weight: normal;
}

.v-card__subtitle,
.v-card__text,
.v-card__title {
  padding: 16px 16px 0px 16px;
  border-bottom: #eee solid 1px;
}

.d-flex {
  display: flex;
}

.flex-wrap {
  flex-wrap: wrap;
}

/* Adjust columns on smaller screens */
@media (max-width: 600px) {
  .v-col {
    padding-left: 4px;
    padding-right: 4px;
  }
}

/* Optional: adjust text size for small screens */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 14px;
  }
}
</style>
